"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CountUp } from "@/components/CountUp";
import { GlowingCard } from "@/components/GlowingCard";
import {
    AnimateSection,
    StaggerContainer,
    StaggerItem,
} from "@/components/AnimateSection";
import {
    extractUsername,
    fetchGitHubUser,
    fetchGitHubRepos,
    fetchGitHubEvents,
    aggregateLanguages,
    totalStars,
    totalForks,
    type GitHubUser,
    type GitHubRepo,
    type GitHubEvent,
} from "@/lib/github";
import type { GitHubCache } from "@/types/database";

/* ──────── language colors ──────── */
const LANG_COLORS: Record<string, string> = {
    JavaScript: "#f1e05a", TypeScript: "#3178c6", Java: "#b07219", Python: "#3572A5",
    Go: "#00ADD8", Rust: "#dea584", "C#": "#178600", "C++": "#f34b7d",
    Ruby: "#701516", PHP: "#4F5D95", Swift: "#F05138", Kotlin: "#A97BFF",
    Dart: "#00B4AB", HTML: "#e34c26", CSS: "#563d7c", Shell: "#89e051",
    Vue: "#41b883", Dockerfile: "#384d54", SCSS: "#c6538c",
};

function getLangColor(lang: string) {
    return LANG_COLORS[lang] || "#8b949e";
}

/* ──────── main component ──────── */
export function GitHubDashboard({
    githubUrl,
    cachedData,
    loading: externalLoading,
    token
}: {
    githubUrl: string;
    cachedData?: GitHubCache | null;
    loading?: boolean;
    token?: string | null;
}) {
    const [user, setUser] = useState<GitHubUser | null>(null);
    const [repos, setRepos] = useState<GitHubRepo[]>([]);
    const [events, setEvents] = useState<GitHubEvent[]>([]);
    const [internalLoading, setInternalLoading] = useState(false);

    useEffect(() => {
        const username = extractUsername(githubUrl);
        if (!username) return;

        if (!cachedData) {
            // No cache: fetch everything
            setInternalLoading(true);
            Promise.all([
                fetchGitHubUser(username, token),
                fetchGitHubRepos(username, token),
                fetchGitHubEvents(username, token),
            ]).then(([u, r, e]) => {
                if (u) setUser(u);
                if (r) setRepos(r);
                if (e) setEvents(e);
                setInternalLoading(false);
            }).catch(() => setInternalLoading(false));
        } else {
            // Have cache for stats, but still fetch live events for freshness
            fetchGitHubEvents(username, token).then((e) => {
                if (e) setEvents(e);
            }).catch(() => { });
        }
    }, [githubUrl, cachedData, token]);

    const isLoading = externalLoading || (!cachedData && internalLoading);

    if (isLoading) {
        return (
            <div className="mx-auto max-w-6xl px-4 py-20 text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-dev-cyan border-t-transparent mx-auto" />
                <p className="mt-4 text-dev-text-muted">Syncing GitHub data...</p>
            </div>
        );
    }

    // Use cached data if available, otherwise use internal local state
    const displayUser = cachedData ? {
        login: cachedData.username,
        public_repos: cachedData.public_repos,
        followers: cachedData.followers
    } : user;

    if (!displayUser) return null;

    const languages = cachedData ? cachedData.top_languages : aggregateLanguages(repos);
    const stars = cachedData ? cachedData.total_stars : totalStars(repos);
    const forks = cachedData ? cachedData.total_forks : totalForks(repos);
    const topRepos = cachedData
        ? [...cachedData.top_repos].sort((a, b) => b.stargazers_count - a.stargazers_count).slice(0, 6)
        : [...repos].sort((a, b) => b.stargazers_count - a.stargazers_count).slice(0, 6);

    // Prefer live events (always fresh across all repos), fallback to cached
    const recentEvents = events.length > 0
        ? events.slice(0, 10)
        : cachedData
            ? cachedData.recent_events.map(e => ({
                id: e.id,
                type: e.type,
                repo: { name: e.repo },
                created_at: e.created_at,
                payload: { commits: [{ message: e.message, sha: "" }] }
            }))
            : [];

    const overviewStats = [
        { label: "Repositories", value: displayUser.public_repos, icon: "📦" },
        { label: "Total Stars", value: stars, icon: "⭐" },
        { label: "Total Forks", value: forks, icon: "🍴" },
        { label: "Followers", value: displayUser.followers, icon: "👥" },
    ];

    const lastSynced = cachedData ? getTimeAgo(cachedData.fetched_at) : null;

    return (
        <AnimateSection className="mx-auto max-w-6xl px-4 py-20">
            <div className="mb-12 text-center">
                <h2 className="text-3xl font-bold text-dev-text-primary sm:text-4xl">
                    GitHub <span className="text-gradient">Activity</span>
                </h2>
                <p className="mt-3 text-dev-text-muted">
                    Real-time data from{" "}
                    <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="text-dev-cyan hover:underline">
                        @{displayUser.login}
                    </a>
                </p>
            </div>

            {/* ═══ Overview Stats ═══ */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {overviewStats.map((s) => (
                    <GlowingCard key={s.label}>
                        <div className="p-5 text-center">
                            <span className="text-2xl">{s.icon}</span>
                            <div className="mt-2 text-2xl font-bold text-dev-text-primary sm:text-3xl">
                                <CountUp end={s.value} duration={2} />
                            </div>
                            <p className="mt-1 text-xs text-dev-text-muted">{s.label}</p>
                        </div>
                    </GlowingCard>
                ))}
            </div>

            {/* ═══ Languages + Top Repos ═══ */}
            <div className="mt-10 grid gap-6 lg:grid-cols-5">
                {/* Language Breakdown */}
                {languages.length > 0 && (
                    <div className="clay-card p-6 lg:col-span-2">
                        <h3 className="mb-4 text-lg font-semibold text-dev-text-primary">🌐 Languages</h3>
                        {/* Bar chart */}
                        <div className="mb-4 flex h-3 overflow-hidden rounded-full">
                            {languages.slice(0, 8).map((lang, i) => (
                                <motion.div
                                    key={lang.name}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${lang.percent}%` }}
                                    transition={{ duration: 0.8, delay: i * 0.1 }}
                                    className="h-full"
                                    style={{ backgroundColor: getLangColor(lang.name) }}
                                    title={`${lang.name}: ${lang.percent}%`}
                                />
                            ))}
                        </div>
                        {/* Legend */}
                        <div className="space-y-2">
                            {languages.slice(0, 8).map((lang) => (
                                <div key={lang.name} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: getLangColor(lang.name) }} />
                                        <span className="text-dev-text-secondary">{lang.name}</span>
                                    </div>
                                    <span className="font-mono text-xs text-dev-text-muted">{lang.percent}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Top Repos */}
                {topRepos.length > 0 && (
                    <div className="lg:col-span-3">
                        <StaggerContainer className="grid gap-3 sm:grid-cols-2">
                            {topRepos.map((repo, idx) => (
                                <StaggerItem key={(repo as GitHubRepo).id || idx} as="div">
                                    <a
                                        href={repo.html_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="clay-card group block h-full p-4 transition-all duration-300 hover:border-dev-cyan/30 hover:scale-[1.02]"
                                    >
                                        <div className="flex items-start justify-between">
                                            <h4 className="font-semibold text-dev-text-primary group-hover:text-dev-cyan transition-colors truncate">
                                                📂 {repo.name}
                                            </h4>
                                        </div>
                                        {repo.description && (
                                            <p className="mt-1.5 text-xs text-dev-text-muted line-clamp-2">{repo.description}</p>
                                        )}
                                        <div className="mt-3 flex items-center gap-3 text-xs text-dev-text-muted">
                                            {repo.language && (
                                                <span className="flex items-center gap-1">
                                                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getLangColor(repo.language) }} />
                                                    {repo.language}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1">⭐ {repo.stargazers_count}</span>
                                            <span className="flex items-center gap-1">🍴 {repo.forks_count}</span>
                                        </div>
                                    </a>
                                </StaggerItem>
                            ))}
                        </StaggerContainer>
                    </div>
                )}
            </div>

            {/* ═══ Recently Updated Repos ═══ */}
            {(() => {
                // Use live repos if available, fall back to cached topRepos
                const allRepos = repos.length > 0 ? repos : topRepos;
                const recentlyUpdated = [...allRepos]
                    .filter(r => r.updated_at)
                    .sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime())
                    .slice(0, 6);

                if (recentlyUpdated.length === 0) return null;

                return (
                    <div className="mt-10 clay-card p-6">
                        <h3 className="mb-5 text-lg font-semibold text-dev-text-primary">⚡ Recent Activity</h3>
                        <div className="space-y-3">
                            {recentlyUpdated.map((repo, idx) => {
                                const repoShort = repo.name;
                                const fullName = repo.html_url?.replace("https://github.com/", "") || `${displayUser.login}/${repo.name}`;
                                return (
                                    <motion.div
                                        key={repo.name}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="flex items-center gap-4 rounded-lg border border-white/5 bg-dev-bg-dark/30 p-4 transition-colors hover:border-dev-cyan/20"
                                    >
                                        {/* Repo icon */}
                                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-dev-blue/20 text-lg">
                                            📂
                                        </div>

                                        {/* Repo info */}
                                        <div className="min-w-0 flex-1">
                                            <a
                                                href={repo.html_url || `https://github.com/${fullName}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-semibold text-sm text-dev-cyan hover:underline"
                                            >
                                                {repoShort}
                                            </a>
                                            {repo.description && (
                                                <p className="mt-0.5 text-xs text-dev-text-muted truncate">{repo.description}</p>
                                            )}
                                            <div className="mt-1.5 flex items-center gap-3 text-xs text-dev-text-muted">
                                                {repo.language && (
                                                    <span className="flex items-center gap-1">
                                                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getLangColor(repo.language) }} />
                                                        {repo.language}
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">⭐ {repo.stargazers_count}</span>
                                                <span className="flex items-center gap-1">🍴 {repo.forks_count}</span>
                                            </div>
                                        </div>

                                        {/* Time */}
                                        <span className="flex-shrink-0 text-xs text-dev-text-muted whitespace-nowrap">
                                            {repo.updated_at ? getTimeAgo(repo.updated_at) : ""}
                                        </span>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}
        </AnimateSection>
    );
}

function getEventDisplay(event: { type: string; payload: { commits?: { message: string; sha: string }[]; ref?: string; action?: string } }) {
    switch (event.type) {
        case "PushEvent": {
            const msg = event.payload.commits?.[0]?.message?.split("\n")[0];
            return { icon: "🔨", action: "Pushed to", detail: msg || null };
        }
        case "CreateEvent":
            return { icon: "✨", action: `Created ${event.payload.ref ? "branch" : "repo"}`, detail: event.payload.ref || null };
        case "DeleteEvent":
            return { icon: "🗑️", action: "Deleted branch in", detail: event.payload.ref || null };
        case "IssuesEvent":
            return { icon: "📋", action: `${event.payload.action === "opened" ? "Opened issue in" : "Closed issue in"}`, detail: null };
        case "PullRequestEvent":
            return { icon: "🔀", action: `${event.payload.action === "opened" ? "Opened PR in" : event.payload.action === "closed" ? "Merged/Closed PR in" : "Updated PR in"}`, detail: null };
        case "WatchEvent":
            return { icon: "⭐", action: "Starred", detail: null };
        case "ForkEvent":
            return { icon: "🍴", action: "Forked", detail: null };
        case "IssueCommentEvent":
            return { icon: "💬", action: "Commented on", detail: null };
        case "ReleaseEvent":
            return { icon: "🚀", action: "Released", detail: null };
        default:
            return { icon: "●", action: "Activity in", detail: null };
    }
}

function getTimeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return `${Math.floor(day / 30)}mo ago`;
}
